/* ==========================================================
   NSG FRAMEWORK
   Componente: NSG Product Experience
   Versión: 2.0.0 · Familias de producto
   Compatible: Shopify Dawn
========================================================== */

(() => {
  'use strict';

  class NSGProductExperience {
    constructor(section) {
      this.section = section;

      this.searchInput = section.querySelector('[data-nsg-product-search]');
      this.productItems = Array.from(
        section.querySelectorAll('[data-nsg-product-item]')
      );
      this.productFamilies = Array.from(
        section.querySelectorAll('[data-nsg-product-family]')
      );
      this.productCount = section.querySelector('[data-nsg-product-count]');
      this.noResults = section.querySelector('[data-nsg-no-results]');
      this.noaButton = section.querySelector('[data-nsg-noa-placeholder]');

      this.searchTimeout = null;

      this.bindEvents();
      this.updateVisibleCount();
      this.updateFamilyVisibility();
    }

    bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          window.clearTimeout(this.searchTimeout);

          this.searchTimeout = window.setTimeout(() => {
            this.filterProducts();
          }, 120);
        });

        this.searchInput.addEventListener('search', () => {
          this.filterProducts();
        });
      }

      this.section.addEventListener('click', (event) => {
        const familyToggle = event.target.closest(
          '[data-nsg-family-toggle]'
        );

        if (familyToggle) {
          event.preventDefault();
          this.toggleFamily(familyToggle);
          return;
        }

        const noaButton = event.target.closest(
          '[data-nsg-noa-placeholder]'
        );

        if (noaButton) {
          event.preventDefault();
          this.handleNoaPlaceholder(noaButton);
        }
      });
    }

    normaliseText(value = '') {
      return String(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    filterProducts() {
      const query = this.normaliseText(this.searchInput?.value || '');
      const terms = query.split(' ').filter(Boolean);
      const isSearchActive = terms.length > 0;

      let visibleProducts = 0;

      this.productItems.forEach((item) => {
        const searchableContent = this.normaliseText(
          [
            item.dataset.productTitle || '',
            item.dataset.productTags || '',
            item.dataset.productType || ''
          ].join(' ')
        );

        const matches =
          !isSearchActive ||
          terms.every((term) => searchableContent.includes(term));

        if (isSearchActive) {
          item.hidden = !matches;
        } else {
          const family = item.closest('[data-nsg-product-family]');
          const familyExpanded =
            family?.querySelector('[data-nsg-family-toggle]')
              ?.getAttribute('aria-expanded') === 'true';

          const familyLimit = Number(
            family?.dataset.familyLimit || 15
          );
          const familyIndex = Number(
            item.dataset.familyIndex || 0
          );

          item.hidden =
            !familyExpanded && familyIndex > familyLimit;
        }

        if (!item.hidden) {
          visibleProducts += 1;
        }
      });

      this.updateFamilyVisibility(isSearchActive);
      this.toggleNoResults(visibleProducts === 0);
      this.updateVisibleCount(visibleProducts);

      document.dispatchEvent(
        new CustomEvent('nsg:product-search:updated', {
          detail: {
            query,
            visibleProducts,
            sectionId: this.section.dataset.sectionId || ''
          }
        })
      );
    }

    toggleFamily(button) {
      const family = button.closest('[data-nsg-product-family]');

      if (!family) {
        return;
      }

      const expanded =
        button.getAttribute('aria-expanded') === 'true';

      const nextExpandedState = !expanded;
      const familyLimit = Number(
        family.dataset.familyLimit || 15
      );

      const familyItems = Array.from(
        family.querySelectorAll('[data-nsg-family-product]')
      );

      familyItems.forEach((item) => {
        const familyIndex = Number(
          item.dataset.familyIndex || 0
        );

        if (nextExpandedState) {
          item.hidden = false;
        } else {
          item.hidden = familyIndex > familyLimit;
        }
      });

      button.setAttribute(
        'aria-expanded',
        String(nextExpandedState)
      );

      const buttonText = button.querySelector(
        '[data-nsg-family-toggle-text]'
      );

      const familyTotal = Number(
        family.dataset.familyTotal || familyItems.length
      );

      if (buttonText) {
        buttonText.textContent = nextExpandedState
          ? 'Mostrar menos'
          : `Ver los ${familyTotal} diseños`;
      }

      if (!nextExpandedState) {
        const familyTop =
          family.getBoundingClientRect().top +
          window.scrollY -
          120;

        window.scrollTo({
          top: familyTop,
          behavior: 'smooth'
        });
      }

      this.updateVisibleCount();
    }

    updateFamilyVisibility(isSearchActive = false) {
      this.productFamilies.forEach((family) => {
        const visibleItems = Array.from(
          family.querySelectorAll('[data-nsg-family-product]')
        ).filter((item) => !item.hidden);

        family.hidden = visibleItems.length === 0;

        const toggle = family.querySelector(
          '[data-nsg-family-toggle]'
        );

        if (toggle) {
          toggle.parentElement.hidden = isSearchActive;
        }
      });
    }

    toggleNoResults(showNoResults) {
      if (this.noResults) {
        this.noResults.hidden = !showNoResults;
      }
    }

    updateVisibleCount(forcedCount = null) {
      if (!this.productCount) {
        return;
      }

      const visibleCount =
        forcedCount !== null
          ? forcedCount
          : this.productItems.filter((item) => !item.hidden).length;

      this.productCount.textContent =
        visibleCount === 1
          ? '1 diseño'
          : `${visibleCount} diseños`;
    }

    handleNoaPlaceholder(button) {
      const originalText = button.textContent.trim();

      button.disabled = true;
      button.textContent = 'Noa estará disponible próximamente';

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1800);

      document.dispatchEvent(
        new CustomEvent('nsg:noa:placeholder-clicked', {
          detail: {
            sectionId: this.section.dataset.sectionId || '',
            collectionHandle:
              this.section.dataset.collectionHandle || ''
          }
        })
      );
    }

    refresh() {
      this.productItems = Array.from(
        this.section.querySelectorAll('[data-nsg-product-item]')
      );

      this.productFamilies = Array.from(
        this.section.querySelectorAll('[data-nsg-product-family]')
      );

      this.filterProducts();

      document.dispatchEvent(
        new CustomEvent('nsg:products:rendered', {
          detail: {
            container: this.section
          }
        })
      );
    }
  }

  const instances = new WeakMap();

  const initialiseSection = (section) => {
    if (!section || instances.has(section)) {
      return;
    }

    const instance = new NSGProductExperience(section);
    instances.set(section, instance);
  };

  const initialiseAllSections = (root = document) => {
    root
      .querySelectorAll('[data-nsg-product-experience]')
      .forEach(initialiseSection);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initialiseAllSections();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAllSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(
      '[data-nsg-product-experience]'
    );

    if (section) {
      instances.delete(section);
    }
  });

  document.addEventListener('nsg:collection:updated', (event) => {
    const section = event.detail?.section;

    if (!section) {
      return;
    }

    const instance = instances.get(section);

    if (instance) {
      instance.refresh();
    } else {
      initialiseSection(section);
    }
  });
})();